import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ArrowDownToLine, ArrowUpFromLine, Coins } from "lucide-react";
import { CryptoIcon } from "../components/icons/CryptoIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, d = 2) =>
  (Number.isFinite(n) ? n : 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

const ASSET_META = {
  TON: { name: "Toncoin", short: "TON" },
  TRX: { name: "Tron", short: "TRX" },
  BNB: { name: "BNB Chain", short: "BNB" },
};

const Wallet = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [prices, setPrices] = useState({ TON: 0, TRX: 0, BNB: 0, USDT: 1 });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/prices`)
      .then((r) => {
        if (!cancelled) setPrices(r.data.prices || {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-refresh user data so admin-side changes appear quickly
  useEffect(() => {
    if (!refreshUser) return;
    refreshUser(); // immediate
    const interval = setInterval(() => {
      // Only poll while tab is visible to avoid useless requests
      if (document.visibilityState === "visible") {
        refreshUser();
      }
    }, 10000); // every 10 seconds
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshUser();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshUser]);

  const balance = user?.balance || 0;
  const tradingBalance = user?.trading_balance || 0;
  const total = balance + tradingBalance;
  const assets = user?.assets || { TON: 0, TRX: 0, BNB: 0 };

  const assetUsdValues = {
    TON: Number(assets.TON || 0) * (prices.TON || 0),
    TRX: Number(assets.TRX || 0) * (prices.TRX || 0),
    BNB: Number(assets.BNB || 0) * (prices.BNB || 0),
  };
  const totalAssetsUsd = assetUsdValues.TON + assetUsdValues.TRX + assetUsdValues.BNB;

  return (
    <div className="min-h-screen bg-[#000000]">
      <div className="border-b border-[#1F1F1F]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm"
            data-testid="wallet-back-btn"
          >
            <ArrowLeft size={18} />
            <span>Назад</span>
          </button>
          <div className="text-white font-bold text-lg tracking-tight">
            Web-<span className="text-[#EDEDED]">3</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <h1 className="font-chivo font-bold text-2xl sm:text-3xl text-white mb-1.5">Кошелёк</h1>
        <p className="text-[#A1A1AA] text-sm mb-6 sm:mb-8">Пополнение баланса и вывод средств</p>

        {/* Balance summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10">
          <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-6">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#71717A] font-medium mb-3">Доступный баланс</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-[26px] sm:text-[30px] leading-none text-white font-semibold tracking-tight" data-testid="wallet-available">{fmt(balance)}</span>
              <span className="inline-flex items-center gap-0.5 text-[#71717A] text-sm font-medium">USDT</span>
            </div>
            <div className="text-[#71717A] text-xs font-mono mt-1.5">≈ ${fmt(balance)}</div>
          </div>
          <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-6">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#71717A] font-medium mb-3">Используется</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-[26px] sm:text-[30px] leading-none text-white font-semibold tracking-tight" data-testid="wallet-trading">{fmt(tradingBalance)}</span>
              <span className="inline-flex items-center gap-0.5 text-[#71717A] text-sm font-medium">USDT</span>
            </div>
            <div className="text-[#71717A] text-xs font-mono mt-1.5">≈ ${fmt(tradingBalance)}</div>
          </div>
          <div className="bg-[#0A0A0A] rounded-xl border border-[#2E2E2E] p-6">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#EDEDED] font-medium mb-3">Всего</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-[26px] sm:text-[30px] leading-none text-white font-semibold tracking-tight" data-testid="wallet-total">{fmt(total)}</span>
              <span className="inline-flex items-center gap-0.5 text-[#71717A] text-sm font-medium">USDT</span>
            </div>
            <div className="text-[#71717A] text-xs font-mono mt-1.5">≈ ${fmt(total)}</div>
          </div>
        </div>

        {/* Two actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <Link
            to="/deposit"
            className="group bg-[#0A0A0C] border border-[#1F1F1F] hover:border-[#2E2E2E] rounded-lg p-8 transition-all hover:bg-[#1A1A1A] flex flex-col"
            data-testid="wallet-deposit-card"
          >
            <div className="w-14 h-14 rounded-lg bg-white/10 text-[#EDEDED] flex items-center justify-center mb-5 group-hover:bg-[#1A1A1A] group-hover:text-white transition-colors">
              <ArrowDownToLine size={26} />
            </div>
            <h3 className="font-chivo font-bold text-xl text-white mb-2">Пополнить счёт</h3>
            <p className="text-[#A1A1AA] text-sm mb-6 flex-1">
              Зачислите средства на ваш баланс. Поддерживаются USDT в сетях TRC-20, BEP-20, ERC-20 и других.
            </p>
            <div className="flex items-center gap-2 text-[#EDEDED] font-semibold text-sm">
              <span>Перейти к пополнению</span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </div>
          </Link>

          <Link
            to="/withdrawal"
            className="group bg-[#0A0A0C] border border-[#1F1F1F] hover:border-[#2E2E2E] rounded-lg p-8 transition-all hover:bg-[#1A1A1A] flex flex-col"
            data-testid="wallet-withdraw-card"
          >
            <div className="w-14 h-14 rounded-lg bg-white/10 text-[#EDEDED] flex items-center justify-center mb-5 group-hover:bg-[#1A1A1A] group-hover:text-white transition-colors">
              <ArrowUpFromLine size={26} />
            </div>
            <h3 className="font-chivo font-bold text-xl text-white mb-2">Вывод средств</h3>
            <p className="text-[#A1A1AA] text-sm mb-6 flex-1">
              Выведите средства на свой внешний кошелёк. Минимальная сумма вывода — $10.
            </p>
            <div className="flex items-center gap-2 text-[#EDEDED] font-semibold text-sm">
              <span>Перейти к выводу</span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </div>
          </Link>
        </div>

        {/* Info */}
        <div className="mt-10 bg-white/5 border border-white/20 rounded-lg p-5">
          <div className="text-[#EDEDED] font-semibold text-sm mb-2">💡 Информация</div>
          <ul className="text-[#A1A1AA] text-sm space-y-1">
            <li>• Пополнения зачисляются автоматически после подтверждения транзакции</li>
            <li>• Выводы обрабатываются в течение 24 часов</li>
            <li>• Все операции защищены и хранятся в истории</li>
          </ul>
        </div>

        {/* My Assets */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-chivo font-bold text-xl text-white flex items-center gap-2">
                <Coins size={20} className="text-[#EDEDED]" />
                Мои активы
              </h2>
              <div className="text-[#A1A1AA] text-xs mt-1">
                Стоимость:{" "}
                <span className="text-white font-mono font-bold">
                  ${fmt(totalAssetsUsd)}
                </span>{" "}
                по текущему курсу
              </div>
            </div>
            <Link to="/deposit?tab=assets" className="text-sm text-[#A1A1AA] hover:text-[#EDEDED] transition-colors">
              Пополнить актив →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(ASSET_META).map(([code, meta]) => {
              const assetBalance = Number(assets[code] || 0);
              const price = prices[code] || 0;
              const usdValue = assetBalance * price;
              return (
                <Link
                  key={code}
                  to={`/deposit?asset=${code}`}
                  className="group bg-[#0A0A0C] border border-[#1F1F1F] hover:border-[#2E2E2E]/60 rounded-lg p-5 transition-colors"
                  data-testid={`asset-card-${code}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <CryptoIcon code={code} size={36} />
                      <div>
                        <div className="text-white font-semibold text-sm">{meta.short}</div>
                        <div className="text-[#71717A] text-[10px]">{meta.name}</div>
                      </div>
                    </div>
                    <span className="text-[#71717A] text-xs group-hover:text-[#EDEDED] transition-colors">+</span>
                  </div>
                  <div className="font-mono text-2xl text-white font-bold">{fmt(assetBalance, 4)}</div>
                  <div className="text-[#A1A1AA] text-xs mt-1 font-mono">
                    ≈ ${fmt(usdValue)}
                    {price > 0 && (
                      <span className="text-[#71717A] ml-2">@ ${price.toFixed(price < 1 ? 4 : 2)}</span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
          <p className="text-[#71717A] text-xs mt-3">
            Балансы активов не участвуют в торговле и хранятся отдельно от USDT-баланса.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Wallet;
